import { supabase } from "@/integrations/supabase/client";
import { classifyBpm, type HrZoneId } from "@/lib/heartRateZones";
import { getActivePhaseWithDistanceAnchors } from "@/lib/runPlanParser";
import {
  buildRacePacingSpeech,
  computeRacePacing,
  formatRaceTimeSpeech,
  isSingleRaceGoalPlan,
  parseRaceGoal,
  type RacePacingState,
} from "@/lib/raceGoal";
import { formatElapsedSpeech, formatPaceSpeech } from "@/lib/formatPace";
import { getStoredUnits, formatDistanceWithUnit, kmToDisplayDistance } from "@/lib/units";

export type CoachFrequency = "rare" | "when_needed" | "frequent" | "watch_only";

export interface RunningCoachGuideSettings {
  enabled: boolean;
  frequency: CoachFrequency;
  plan: string;
  source?: "manual" | "arnold";
}

export const normalizeCoachPlanText = (plan: unknown): string => {
  if (typeof plan === "string") return plan;
  if (plan == null) return "";
  return String(plan);
};

export type RunningLaunchSettings = {
  activityType: string;
  audioStats: string;
  audioVoice?: "female" | "male";
  gpsActivity?: "run" | "walk" | "hike";
  startTime?: string;
  coachGuide?: RunningCoachGuideSettings;
};

const defaultLaunchSettings = (): RunningLaunchSettings => ({
  activityType: "outdoor",
  audioStats: "5min",
  audioVoice: "female",
  gpsActivity: "run",
});

export const parseRunningSettingsFromStorage = (raw: string | null): RunningLaunchSettings => {
  if (!raw) return defaultLaunchSettings();
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const cg = parsed.coachGuide as Record<string, unknown> | undefined;
    const frequency = cg?.frequency;
    const coachGuide: RunningCoachGuideSettings | undefined = cg
      ? {
          enabled: !!cg.enabled,
          frequency:
            frequency === "rare" ||
            frequency === "when_needed" ||
            frequency === "frequent" ||
            frequency === "watch_only"
              ? frequency
              : "when_needed",
          plan: normalizeCoachPlanText(cg.plan),
          source: cg.source === "arnold" ? "arnold" : "manual",
        }
      : undefined;
    const gps = parsed.gpsActivity;
    return {
      activityType: typeof parsed.activityType === "string" ? parsed.activityType : "outdoor",
      audioStats: typeof parsed.audioStats === "string" ? parsed.audioStats : "5min",
      audioVoice: parsed.audioVoice === "male" ? "male" : "female",
      startTime: typeof parsed.startTime === "string" ? parsed.startTime : undefined,
      gpsActivity: gps === "walk" || gps === "hike" ? gps : "run",
      coachGuide,
    };
  } catch {
    return defaultLaunchSettings();
  }
};

export const buildRunningLaunchSettings = (
  input: {
    activityType: string;
    audioStats: string;
    audioVoice?: "female" | "male";
    gpsActivity?: "run" | "walk" | "hike";
    coachGuide: RunningCoachGuideSettings;
    startTime: string;
  },
): RunningLaunchSettings => {
  const plan = normalizeCoachPlanText(input.coachGuide.plan);
  return {
    activityType: input.activityType,
    audioStats: input.audioStats,
    audioVoice: input.audioVoice,
    gpsActivity: input.gpsActivity,
    startTime: input.startTime,
    coachGuide: {
      ...input.coachGuide,
      plan,
      enabled: !!(input.coachGuide.enabled && plan.trim()),
    },
  };
};

export interface ParsedRunPlan {
  raw: string;
  targetDistanceKm?: number;
  targetTimeSeconds?: number;
  targetPaceSecondsPerKm?: number;
  intervalDistanceKm?: number;
}

export type PaceVerdict = "on_target" | "too_fast" | "too_slow" | "unknown";

export interface RunningCoachSnapshot {
  plan: string;
  frequency: CoachFrequency;
  distanceKm: number;
  totalDistanceMeters?: number;
  elapsedSeconds: number;
  /** Human-readable elapsed time with seconds, e.g. "23 minutes and 46 seconds". */
  elapsedSpeech?: string;
  currentPaceSecondsPerKm: number;
  averagePaceSecondsPerKm: number;
  lastSplitSeconds?: number;
  paceDeltaSeconds?: number;
  /** Phase-specific target pace (sec/km) from the structured plan. */
  targetPaceSecondsPerKm?: number;
  paceVerdict?: PaceVerdict;
  currentBpm?: number | null;
  targetZone?: HrZoneId | null;
  zones?: Array<{ id: string; label?: string; lowerBpm: number; upperBpm: number }>;
  activePhaseLabel?: string;
  activePhaseSummary?: string;
  nextPhaseLabel?: string;
  phaseKind?: string;
  remainingPhaseSeconds?: number;
  /** Seconds elapsed in the current timed phase block. */
  phaseElapsedSeconds?: number;
  remainingPhaseDistanceMeters?: number;
  /** Meters into the current distance block (interval), excluding prior warm-up / recovery jog. */
  phaseDistanceIntoMeters?: number;
  /** Total watch distance (m) when the current distance block ends. */
  phaseCompleteAtDistanceMeters?: number;
  /** Total watch distance (m) when the current block started. */
  phaseStartDistanceMeters?: number;
  intervalRep?: number;
  intervalTotalReps?: number;
  isLastInterval?: boolean;
  /** Timed distance race goal (10K in 40 min, half marathon in 2 hours, etc.). */
  raceLabel?: string;
  targetDistanceKm?: number;
  targetTimeSeconds?: number;
  projectedFinishSeconds?: number;
  scheduleDeltaSeconds?: number;
  requiredPaceSecondsPerKm?: number;
}

/** Phases where interval pace targets must not apply. */
const NON_WORK_PHASE_KINDS = new Set(["warmup", "cooldown", "recovery", "easy"]);

/** Target pace for live coaching — never fall back to plan-wide interval pace during warm-up / recovery. */
export function resolveLiveCoachTargetPace(
  phaseKind: string | undefined,
  phasePaceSecondsPerKm: number | undefined,
  planText: string,
): number | undefined {
  if (phasePaceSecondsPerKm && phasePaceSecondsPerKm > 0) {
    return phasePaceSecondsPerKm;
  }
  if (!phaseKind || NON_WORK_PHASE_KINDS.has(phaseKind)) {
    return undefined;
  }
  const parsed = parseRunningPlan(planText);
  return parsed.targetPaceSecondsPerKm;
}

/** Expand abbreviations for natural TTS — say "kilometer"/"mile" not "km". */
export function sanitizeCoachSpeechText(text: string): string {
  const units = getStoredUnits();
  const paceUnit = units === "imperial" ? "mile" : "kilometer";
  return String(text || "")
    .replace(/\bZ([1-5])\b/g, "Zone $1")
    .replace(/\bzone\s*([1-5])\b/gi, (_, n) => `Zone ${n}`)
    .replace(/(\d{1,2}):60\b/g, (_, m) => `${parseInt(m, 10) + 1}:00`)
    .replace(/(\d+(?:\.\d+)?)\s*km\b/gi, (_, n) => {
      const v = parseFloat(n);
      if (units === "imperial") {
        const mi = kmToDisplayDistance(v, "imperial");
        const rounded = Math.round(mi * 10) / 10;
        return `${rounded} mile${rounded === 1 ? "" : "s"}`;
      }
      return `${n} kilometer${v === 1 ? "" : "s"}`;
    })
    .replace(/\bkm\b/gi, units === "imperial" ? "mile" : "kilometer")
    .replace(/\bkm\/h\b/gi, units === "imperial" ? "miles per hour" : "kilometers per hour")
    .replace(/(\d{1,2}):(\d{2})\s*\/\s*km\b/gi, (_, m, s) => {
      const totalSec = parseInt(m, 10) * 60 + parseInt(s, 10);
      return formatPaceSpeech(totalSec, units);
    })
    .replace(/(\d{1,2}):(\d{2})\s*seconds?\s*per\s*kilometer/gi, (_, m, s) =>
      formatPaceSpeech(parseInt(m, 10) * 60 + parseInt(s, 10), units),
    )
    .replace(new RegExp(`per ${paceUnit}`, "gi"), `per ${paceUnit}`)
    .replace(/\s+/g, " ")
    .trim();
}

const parseNumber = (value: string) => Number(value.replace(",", "."));

export function parseRunningPlan(plan: string): ParsedRunPlan {
  const parsed: ParsedRunPlan = { raw: plan };
  const race = parseRaceGoal(plan);
  if (race) {
    parsed.targetDistanceKm = race.distanceKm;
    parsed.targetTimeSeconds = race.durationSeconds;
    parsed.targetPaceSecondsPerKm = race.paceSecondsPerKm;
    return parsed;
  }

  const text = plan.toLowerCase();

  const distanceMatch = text.match(/(\d+(?:[.,]\d+)?)\s*k(?:m|ilometer)/);
  if (distanceMatch) parsed.targetDistanceKm = parseNumber(distanceMatch[1]);

  const goalMatch = text.match(
    /(\d+(?:[.,]\d+)?)\s*k(?:m|ilometer)?\s*(?:at|in|for|within|under)\s*(\d+(?:[.,]\d+)?)\s*(?:min(?:utes?)?|m\b)/,
  );
  if (goalMatch) {
    parsed.targetDistanceKm = parseNumber(goalMatch[1]);
    parsed.targetTimeSeconds = Math.round(parseNumber(goalMatch[2]) * 60);
  } else {
    const timeMatch = text.match(/\b(?:in|under|within|for|at)\s*(\d{1,2})\s*(?:min(?:utes?)?|m\b)/);
    if (timeMatch) parsed.targetTimeSeconds = Number(timeMatch[1]) * 60;
  }

  const paceColon = text.match(/(\d{1,2}):(\d{2})\s*(?:pace|\/?\s*km|per\s*k(?:m|ilometer))/);
  if (paceColon) {
    parsed.targetPaceSecondsPerKm = Number(paceColon[1]) * 60 + Number(paceColon[2]);
  }
  if (!parsed.targetPaceSecondsPerKm) {
    const paceMinKm = text.match(/(\d+(?:[.,]\d+)?)\s*min\s*(?:\/|per)\s*k(?:m|ilometer)?/);
    if (paceMinKm) parsed.targetPaceSecondsPerKm = Math.round(parseNumber(paceMinKm[1]) * 60);
  }

  const intervalMatch = text.match(/(\d+(?:[.,]\d+)?)\s*k(?:m|ilometer)?\s*interval/);
  if (intervalMatch) parsed.intervalDistanceKm = parseNumber(intervalMatch[1]);

  if (!parsed.targetPaceSecondsPerKm && parsed.targetDistanceKm && parsed.targetTimeSeconds) {
    parsed.targetPaceSecondsPerKm = parsed.targetTimeSeconds / parsed.targetDistanceKm;
  }

  return parsed;
}

export function getCoachMinIntervalSeconds(frequency: CoachFrequency): number {
  if (frequency === "frequent") return 30;
  if (frequency === "when_needed") return 90;
  if (frequency === "rare") return 240;
  if (frequency === "watch_only") return 99999;
  return 90;
}


function zoneLabel(zones: RunningCoachSnapshot["zones"], id: HrZoneId): string {
  const z = zones?.find((x) => x.id === id);
  const zoneNum = id.replace(/^z/i, "");
  const base = `Zone ${zoneNum}`;
  if (!z?.label) return base;
  const name = z.label.replace(/^Z\d\s*/i, "").trim();
  return name ? `${base} ${name}` : base;
}

/** Compare live pace to the phase target. Negative delta = faster than target. */
export function comparePaceToTarget(
  currentSecPerKm: number,
  targetSecPerKm: number,
  toleranceSec = 8,
): { verdict: PaceVerdict; deltaSec: number; message: string } {
  if (
    !currentSecPerKm ||
    !targetSecPerKm ||
    !Number.isFinite(currentSecPerKm) ||
    !Number.isFinite(targetSecPerKm) ||
    currentSecPerKm <= 0 ||
    targetSecPerKm <= 0
  ) {
    return { verdict: "unknown", deltaSec: 0, message: "" };
  }

  const units = getStoredUnits();
  const delta = Math.round(currentSecPerKm - targetSecPerKm);
  const cur = formatPaceSpeech(currentSecPerKm, units);
  const tgt = formatPaceSpeech(targetSecPerKm, units);

  if (Math.abs(delta) <= toleranceSec) {
    return {
      verdict: "on_target",
      deltaSec: delta,
      message: `Pace on target at roughly ${cur}.`,
    };
  }
  if (delta < 0) {
    return {
      verdict: "too_fast",
      deltaSec: delta,
      message: `Too fast at about ${cur} — ease toward ${tgt}.`,
    };
  }
  return {
    verdict: "too_slow",
    deltaSec: delta,
    message: `Too slow at about ${cur} — lift toward ${tgt}.`,
  };
}

function buildHrFeedback(snapshot: RunningCoachSnapshot): string {
  if (!snapshot.currentBpm || !snapshot.zones?.length) return "";

  const zones = snapshot.zones.map((z) => ({
    id: z.id as HrZoneId,
    label: z.label || z.id,
    lowerBpm: z.lowerBpm,
    upperBpm: z.upperBpm,
  }));
  const currentZone = classifyBpm(snapshot.currentBpm, zones);
  if (!currentZone) return "";

  if (!snapshot.targetZone) {
    return `Heart rate ${Math.round(snapshot.currentBpm)}.`;
  }

  if (currentZone === snapshot.targetZone) {
    return `Heart rate ${Math.round(snapshot.currentBpm)} — in target ${zoneLabel(snapshot.zones, snapshot.targetZone)}.`;
  }

  const target = zones.find((x) => x.id === snapshot.targetZone);
  if (!target) return "";

  const zoneIdx = (id: HrZoneId) => zones.findIndex((x) => x.id === id);
  if (zoneIdx(currentZone) > zoneIdx(snapshot.targetZone)) {
    return `Heart rate ${Math.round(snapshot.currentBpm)} is high — ease toward ${zoneLabel(snapshot.zones, snapshot.targetZone)}.`;
  }
  return `Heart rate ${Math.round(snapshot.currentBpm)} is low — lift toward ${zoneLabel(snapshot.zones, snapshot.targetZone)}.`;
}

function hrOutOfZone(snapshot: RunningCoachSnapshot): boolean {
  if (!snapshot.currentBpm || !snapshot.targetZone || !snapshot.zones?.length) return false;
  const zones = snapshot.zones.map((z) => ({
    id: z.id as HrZoneId,
    label: z.label || z.id,
    lowerBpm: z.lowerBpm,
    upperBpm: z.upperBpm,
  }));
  const currentZone = classifyBpm(snapshot.currentBpm, zones);
  return !!currentZone && currentZone !== snapshot.targetZone;
}

function pickVariant(seed: number, options: string[]): string {
  if (options.length === 0) return "";
  return options[Math.abs(seed) % options.length];
}

/** Build race pacing fields for live coach snapshot when in a timed distance race. */
export function resolveRacePacingForSnapshot(
  plan: string,
  phaseKind: string | undefined,
  phaseTargetTimeSeconds: number | undefined,
  phaseDistanceKm: number | undefined,
  phasePaceSecondsPerKm: number | undefined,
  distanceKm: number,
  elapsedSeconds: number,
  currentPaceSecondsPerKm: number,
): (RacePacingState & { raceLabel: string }) | null {
  if (phaseKind === 'interval' || phaseKind === 'recovery' || phaseKind === 'warmup' || phaseKind === 'cooldown') {
    return null;
  }
  if (!isSingleRaceGoalPlan(plan)) return null;

  const race = parseRaceGoal(plan);
  if (!race) return null;
  const pacing = computeRacePacing(race, distanceKm, elapsedSeconds, currentPaceSecondsPerKm);
  if (!pacing) return null;
  return { ...pacing, raceLabel: race.label };
}

export type WatchCoachGuideMetricMode = 'distance' | 'time';

export type WatchCoachGuidePayload = {
  active: boolean;
  headline: string;
  isRest: boolean;
  metricMode: WatchCoachGuideMetricMode;
  /** Planned block length in meters (distance mode). */
  distanceMeters?: number;
  /** Total run meters when this block started — watch derives IN BLOCK / LEFT locally. */
  blockStartDistanceMeters?: number;
  distanceIntoBlockMeters?: number;
  remainingDistanceMeters?: number;
  remainingSeconds?: number;
  /** Run elapsed (sec) when the current timed block ends — watch derives smooth countdown. */
  blockEndsAtRunElapsed?: number;
  isComplete?: boolean;
  completeSummary?: string;
};

/** Stable key for phase metadata — ignores per-meter into/remaining so we don't flood WCSession. */
export function watchCoachGuideStructureKey(guide: WatchCoachGuidePayload): string {
  return [
    guide.active ? "1" : "0",
    guide.isComplete ? "1" : "0",
    guide.headline,
    guide.isRest ? "1" : "0",
    guide.metricMode,
    guide.distanceMeters != null ? Math.round(guide.distanceMeters) : "",
    guide.blockStartDistanceMeters != null ? Math.round(guide.blockStartDistanceMeters) : "",
    guide.blockEndsAtRunElapsed != null ? Math.round(guide.blockEndsAtRunElapsed) : "",
    guide.completeSummary ?? "",
  ].join("|");
}

/** Round distance to 0.1 m for watch guide labels. */
export function roundGuideDistanceMeters(km: number): number {
  return Math.max(0, Math.round(km * 10000) / 10);
}

/** Prefer freshly built local guidance when cloud round-trip would stale timing. */
export function pickCoachSpeechGuidance(
  cloudGuidance: string | null,
  requestSnapshot: RunningCoachSnapshot,
  freshSnapshot: RunningCoachSnapshot,
  freshFallback: string | null,
  requestStartedAtMs: number,
): string | null {
  const requestAgeMs = Date.now() - requestStartedAtMs;
  const elapsedDrift = Math.abs(freshSnapshot.elapsedSeconds - requestSnapshot.elapsedSeconds);
  const paceDrift =
    Math.abs(
      (freshSnapshot.currentPaceSecondsPerKm || 0) - (requestSnapshot.currentPaceSecondsPerKm || 0),
    ) >= 15;
  const stale = requestAgeMs > 500 || elapsedDrift >= 2 || paceDrift;
  if (stale && freshFallback) return freshFallback;
  return cloudGuidance?.trim() || freshFallback;
}

export function buildWorkoutCompleteSpeech(elapsedSeconds: number, distanceKm: number): string {
  const time = formatElapsedSpeech(elapsedSeconds);
  const units = getStoredUnits();
  const dist =
    distanceKm >= 0.1
      ? formatDistanceWithUnit(distanceKm, units).replace(/\bmi\b/, "miles").replace(/\bkm\b/, "kilometers")
      : units === "imperial"
        ? `${Math.max(0, Math.round(distanceKm * 1000 * 3.28084))} feet`
        : `${Math.max(0, Math.round(distanceKm * 1000))} meters`;
  return `Workout complete! ${time} over ${dist}. Outstanding effort — take a moment to cool down and recover well.`;
}

export function buildWorkoutCompleteGuidePayload(
  elapsedSeconds: number,
  distanceKm: number,
): WatchCoachGuidePayload {
  const time = formatElapsedSpeech(elapsedSeconds);
  const distKm = distanceKm >= 1 ? `${distanceKm.toFixed(1).replace(/\.0$/, '')} km` : `${Math.round(distanceKm * 1000)} m`;
  return {
    active: false,
    isComplete: true,
    headline: 'Workout complete!',
    completeSummary: `${time} · ${distKm}`,
    isRest: false,
    metricMode: 'time',
    remainingSeconds: 0,
    distanceMeters: Math.round(distanceKm * 1000),
  };
}

/** Build live AI guide state for Apple Watch from the active workout phase. */
export function buildWatchCoachGuidePayload(args: {
  plan: string;
  structure: import('./runPlanParser').ParsedRunStructure;
  elapsedSeconds: number;
  distanceKm: number;
  phaseStartDistanceKmByIndex: Map<number, number>;
  phaseEndElapsedSecondsByIndex: Map<number, number>;
  lastPhaseIndexRef: { current: number };
  phaseStartElapsedSecondsByIndex?: Map<number, number>;
}): WatchCoachGuidePayload | null {
  const {
    plan,
    structure,
    elapsedSeconds,
    distanceKm,
    phaseStartDistanceKmByIndex,
    phaseEndElapsedSecondsByIndex,
    lastPhaseIndexRef,
    phaseStartElapsedSecondsByIndex,
  } = args;

  const active = getActivePhaseWithDistanceAnchors(
    structure,
    elapsedSeconds,
    distanceKm,
    phaseStartDistanceKmByIndex,
    phaseEndElapsedSecondsByIndex,
    lastPhaseIndexRef,
    phaseStartElapsedSecondsByIndex,
  );
  if (!active) {
    return { active: true, headline: 'Guide', isRest: false, metricMode: 'distance' };
  }

  const phaseKind = active.phase.kind;
  const isRest = phaseKind === 'recovery';
  const isDistancePhase = (active.phase.distanceKm ?? 0) > 0;
  const isTimeBlock =
    isRest ||
    (!isDistancePhase &&
      (active.remainingSeconds != null ||
        (active.phase.durationSeconds ?? 0) > 0));
  const metricMode: WatchCoachGuideMetricMode = isTimeBlock ? 'time' : 'distance';

  let headline = active.phase.label;
  if (isRest) headline = 'Rest';
  else if (phaseKind === 'interval' && active.phase.repInSet) {
    headline = `Interval ${active.phase.repInSet.rep}`;
  } else if (isSingleRaceGoalPlan(plan)) {
    const race = parseRaceGoal(plan);
    if (race) headline = race.label;
  }

  const blockDistanceMeters =
    isDistancePhase && active.phase.distanceKm
      ? roundGuideDistanceMeters(active.phase.distanceKm)
      : undefined;
  const remainingDistanceMeters =
    active.remainingDistanceKm != null
      ? roundGuideDistanceMeters(active.remainingDistanceKm)
      : undefined;
  const distanceIntoBlockMeters =
    active.distanceIntoPhaseKm != null
      ? roundGuideDistanceMeters(active.distanceIntoPhaseKm)
      : undefined;

  let remainingSeconds = active.remainingSeconds ?? undefined;
  if (remainingSeconds != null && (remainingSeconds <= 0 || remainingSeconds > 7200)) {
    remainingSeconds = undefined;
  }

  const blockEndsAtRunElapsed =
    metricMode === 'time' && remainingSeconds != null && remainingSeconds > 0
      ? elapsedSeconds + remainingSeconds
      : undefined;

  return {
    active: true,
    headline,
    isRest,
    metricMode,
    distanceMeters: metricMode === 'distance' ? blockDistanceMeters : undefined,
    blockStartDistanceMeters:
      metricMode === 'distance' ? active.phaseStartDistanceMeters : undefined,
    distanceIntoBlockMeters: metricMode === 'distance' ? distanceIntoBlockMeters : undefined,
    remainingDistanceMeters: metricMode === 'distance' ? remainingDistanceMeters : undefined,
    remainingSeconds: metricMode === 'time' ? remainingSeconds : undefined,
    blockEndsAtRunElapsed,
  };
}

/** Prevent watch IN BLOCK from jumping backward when GPS snaps. */
export function stabilizeWatchCoachGuidePayload(
  guide: WatchCoachGuidePayload | null,
  phaseIndex: number,
  maxIntoByPhaseRef: { current: Map<number, number> },
): WatchCoachGuidePayload | null {
  if (!guide || guide.distanceIntoBlockMeters == null || phaseIndex < 0) return guide;
  const prev = maxIntoByPhaseRef.current.get(phaseIndex) ?? 0;
  const into = Math.max(prev, guide.distanceIntoBlockMeters);
  maxIntoByPhaseRef.current.set(phaseIndex, into);
  if (into === guide.distanceIntoBlockMeters) return guide;
  return {
    ...guide,
    distanceIntoBlockMeters: into,
    remainingDistanceMeters:
      guide.distanceMeters != null
        ? Math.max(0, guide.distanceMeters - into)
        : guide.remainingDistanceMeters,
  };
}

/** Time/distance race check-in when GPS is weak or no pace/HR cue fired yet. */
export function buildElapsedRaceCheckIn(
  plan: string,
  elapsedSeconds: number,
  distanceKm: number,
): string | null {
  if (elapsedSeconds < 45) return null;
  if (!isSingleRaceGoalPlan(plan)) return null;
  const race = parseRaceGoal(plan);
  if (!race) return null;

  const targetTimeSpeech = formatRaceTimeSpeech(race.durationSeconds);
  const targetPaceSpeech = formatPaceSpeech(race.paceSecondsPerKm, getStoredUnits());
  const elapsedSpeech = formatElapsedSpeech(elapsedSeconds);
  const progressPct = Math.round((elapsedSeconds / race.durationSeconds) * 100);

  if (distanceKm < 0.02) {
    return `${race.label}: ${elapsedSpeech} in — about ${progressPct}% through your ${targetTimeSpeech} target. Hold around ${targetPaceSpeech} once GPS settles.`;
  }

  const expectedKm = (elapsedSeconds / race.durationSeconds) * race.distanceKm;
  const deltaKm = distanceKm - expectedKm;
  const remKm = Math.max(0, race.distanceKm - distanceKm);
  const units = getStoredUnits();
  const rem =
    remKm >= 0.1
      ? `${formatDistanceWithUnit(remKm, units).replace(/\bmi\b/, "miles").replace(/\bkm\b/, "kilometers")} left`
      : units === "imperial"
        ? `${Math.round(remKm * 1000 * 3.28084)} feet left`
        : `${Math.round(remKm * 1000)} meters left`;

  if (Math.abs(deltaKm) <= 0.04) {
    return `${race.label} on schedule at ${elapsedSpeech} — ${rem}. Hold about ${targetPaceSpeech} for ${targetTimeSpeech}.`;
  }
  if (deltaKm < -0.04) {
    return `${race.label}: a bit behind schedule at ${elapsedSpeech} — ${rem}. Lift slightly toward ${targetPaceSpeech}.`;
  }
  return `${race.label}: ${Math.round(deltaKm * 1000)} meters ahead of schedule at ${elapsedSpeech} — ${rem}. Ease to ${targetPaceSpeech}.`;
}

/** Rotating extra facts so check-ins aren't only pace + HR. */
function buildExtraCheckInFact(snapshot: RunningCoachSnapshot): string | null {
  const units = getStoredUnits();
  const seed = Math.floor(snapshot.elapsedSeconds / 15) + Math.round(snapshot.distanceKm * 50);
  const facts: string[] = [];

  if (
    snapshot.remainingPhaseSeconds != null &&
    snapshot.remainingPhaseSeconds > 45 &&
    snapshot.remainingPhaseSeconds <= 240
  ) {
    facts.push(`${Math.round(snapshot.remainingPhaseSeconds)} seconds left in this block.`);
  }
  if (
    snapshot.remainingPhaseDistanceMeters != null &&
    snapshot.remainingPhaseDistanceMeters > 250 &&
    snapshot.remainingPhaseDistanceMeters <= 800
  ) {
    facts.push(`${Math.round(snapshot.remainingPhaseDistanceMeters)} meters left in this block.`);
  }
  if (snapshot.currentBpm && snapshot.currentBpm > 0) {
    facts.push(`Heart rate ${Math.round(snapshot.currentBpm)}.`);
  }
  if (snapshot.distanceKm > 0.2) {
    const dist =
      units === "imperial"
        ? `${(snapshot.distanceKm / 1.609344).toFixed(2)} miles so far`
        : `${snapshot.distanceKm.toFixed(2)} kilometers so far`;
    facts.push(dist);
  }
  if (snapshot.elapsedSeconds >= 60) {
    facts.push(`${formatElapsedSpeech(snapshot.elapsedSeconds)} elapsed.`);
  }
  if (
    snapshot.averagePaceSecondsPerKm &&
    snapshot.averagePaceSecondsPerKm > 0 &&
    snapshot.averagePaceSecondsPerKm <= 900
  ) {
    facts.push(`Average pace about ${formatPaceSpeech(snapshot.averagePaceSecondsPerKm, units)}.`);
  }
  if (snapshot.activePhaseLabel?.trim()) {
    facts.push(`Still in ${snapshot.activePhaseLabel.trim()}.`);
  }

  if (facts.length === 0) return null;
  return facts[seed % facts.length] ?? facts[0];
}

/**
 * Short live-coach cues. Prefer one focus (remaining / pace / HR) so cloud TTS
 * finishes quickly and spoken numbers stay close to the watch.
 *
 * - `watch_only`: never speaks — guidance still reaches the watch via the guide payload.
 * - `rare`: only speaks when off-target (or a remaining-time/distance cue fires above).
 * - `when_needed` / `frequent`: always return a short check-in every tick, slightly
 *   richer than a bare pace callout (pace + one more fact) when off-target correction
 *   isn't needed.
 */
export function buildLocalRunningGuidance(snapshot: RunningCoachSnapshot): string | null {
  if (snapshot.frequency === "watch_only") return null;
  if (snapshot.elapsedSeconds < 20) return null;

  const seed = Math.floor(snapshot.elapsedSeconds / 10) + Math.round(snapshot.distanceKm * 100);
  const curPace = snapshot.currentPaceSecondsPerKm;
  const hasPace = curPace > 0 && curPace <= 900 && Number.isFinite(curPace);

  // Short remaining cues — these TTS fast and match the watch well.
  if (
    snapshot.remainingPhaseDistanceMeters != null &&
    snapshot.remainingPhaseDistanceMeters <= 250 &&
    snapshot.remainingPhaseDistanceMeters > 40
  ) {
    const m = Math.round(snapshot.remainingPhaseDistanceMeters);
    return pickVariant(seed, [
      `${m} meters left in this block.`,
      `${m} meters to go — stay focused.`,
      `About ${m} meters left.`,
    ]);
  }
  if (
    snapshot.remainingPhaseSeconds != null &&
    snapshot.remainingPhaseSeconds <= 45 &&
    snapshot.remainingPhaseSeconds > 5
  ) {
    const s = Math.round(snapshot.remainingPhaseSeconds);
    return pickVariant(seed, [
      `${s} seconds left in this block.`,
      `${s} seconds to go.`,
      `Almost there — ${s} seconds left.`,
    ]);
  }

  const racePacing =
    snapshot.targetDistanceKm &&
    snapshot.targetTimeSeconds &&
    snapshot.scheduleDeltaSeconds != null
      ? {
          targetDistanceKm: snapshot.targetDistanceKm,
          targetTimeSeconds: snapshot.targetTimeSeconds,
          targetPaceSecondsPerKm: snapshot.targetPaceSecondsPerKm ?? 0,
          projectedFinishSeconds: snapshot.projectedFinishSeconds ?? 0,
          scheduleDeltaSeconds: snapshot.scheduleDeltaSeconds,
          requiredPaceSecondsPerKm: snapshot.requiredPaceSecondsPerKm ?? 0,
          remainingDistanceKm: Math.max(0, snapshot.targetDistanceKm - snapshot.distanceKm),
          progressRatio: snapshot.targetDistanceKm > 0 ? snapshot.distanceKm / snapshot.targetDistanceKm : 0,
        }
      : null;

  if (
    racePacing &&
    snapshot.targetPaceSecondsPerKm &&
    hasPace &&
    isSingleRaceGoalPlan(snapshot.plan)
  ) {
    const targetTimeSpeech = formatRaceTimeSpeech(snapshot.targetTimeSeconds!);
    const raceSpeech = buildRacePacingSpeech(
      racePacing,
      snapshot.raceLabel ?? "Race",
      targetTimeSpeech,
      formatPaceSpeech(curPace, getStoredUnits()),
      formatPaceSpeech(snapshot.targetPaceSecondsPerKm, getStoredUnits()),
    );
    if (raceSpeech) return raceSpeech;
  }

  const targetPace =
    snapshot.targetPaceSecondsPerKm && snapshot.targetPaceSecondsPerKm > 0
      ? snapshot.targetPaceSecondsPerKm
      : undefined;
  const skipPaceCompare =
    !targetPace ||
    !hasPace ||
    (snapshot.phaseKind && NON_WORK_PHASE_KINDS.has(snapshot.phaseKind));

  const paceCompare =
    !skipPaceCompare && targetPace ? comparePaceToTarget(curPace, targetPace) : null;
  const paceOff =
    paceCompare &&
    (paceCompare.verdict === "too_fast" || paceCompare.verdict === "too_slow");
  const hrOff = hrOutOfZone(snapshot);

  // rare: only speak when something needs correcting (or remaining cue above).
  if (snapshot.frequency === "rare") {
    if (paceOff && paceCompare?.message) return paceCompare.message;
    if (hrOff) {
      const hr = buildHrFeedback(snapshot);
      if (hr) return hr;
    }
    return null;
  }

  // when_needed / frequent: rotate focus so users don't only hear pace + HR.
  const units = getStoredUnits();
  const focus = seed % 5;
  const extraFact = buildExtraCheckInFact(snapshot);

  if (paceOff && paceCompare?.message && (focus === 0 || focus === 1)) {
    return paceCompare.message;
  }
  if (hrOff && (focus === 2 || focus === 0)) {
    const hr = buildHrFeedback(snapshot);
    if (hr) return hr;
  }
  if (paceOff && paceCompare?.message) return paceCompare.message;
  if (hrOff) {
    const hr = buildHrFeedback(snapshot);
    if (hr) return hr;
  }

  // Distance / elapsed check-ins interleaved with pace
  if (focus === 3 && snapshot.distanceKm > 0.2) {
    const dist =
      units === "imperial"
        ? `${(snapshot.distanceKm / 1.609344).toFixed(2)} miles`
        : `${snapshot.distanceKm.toFixed(2)} kilometers`;
    const base = pickVariant(seed, [
      `You've covered ${dist}.`,
      `${dist} done — keep it rolling.`,
      `Distance so far: ${dist}.`,
    ]);
    return extraFact && !extraFact.includes("so far") ? `${base} ${extraFact}` : base;
  }
  if (focus === 4 && snapshot.elapsedSeconds >= 90) {
    const base = pickVariant(seed, [
      `${formatElapsedSpeech(snapshot.elapsedSeconds)} into the run.`,
      `Time so far: ${formatElapsedSpeech(snapshot.elapsedSeconds)}.`,
    ]);
    return extraFact ? `${base} ${extraFact}` : base;
  }

  if (hasPace && targetPace && paceCompare?.verdict === "on_target") {
    const base = pickVariant(seed, [
      `Pace on target at roughly ${formatPaceSpeech(curPace, units)}.`,
      `Holding about ${formatPaceSpeech(curPace, units)} — good.`,
      `Nice — about ${formatPaceSpeech(curPace, units)}, stay here.`,
    ]);
    return extraFact ? `${base} ${extraFact}` : base;
  }
  if (hasPace) {
    const base = pickVariant(seed, [
      `Your current pace is roughly ${formatPaceSpeech(curPace, units)}.`,
      `You're at about ${formatPaceSpeech(curPace, units)}.`,
      `Pace is roughly ${formatPaceSpeech(curPace, units)}.`,
    ]);
    return extraFact ? `${base} ${extraFact}` : base;
  }
  if (extraFact) return extraFact;
  return pickVariant(seed, [
    "No current pace — keep moving when ready.",
    "GPS settling — find your rhythm.",
    "Looking solid — keep rhythm.",
  ]);
}

export async function requestLiveRunningGuidance(
  snapshot: RunningCoachSnapshot,
  fallback: string | null,
): Promise<string | null> {
  if (!fallback) return null;

  const timeout = new Promise<{ guidance?: string }>((resolve) => {
    window.setTimeout(() => resolve({ guidance: fallback ?? undefined }), 3500);
  });

  const request = supabase.functions
    .invoke("running-live-coach", {
      body: {
        ...snapshot,
        fallback,
      },
    })
    .then(({ data, error }) => {
      if (error) return { guidance: fallback ?? undefined };
      return data as { guidance?: string };
    })
    .catch(() => ({ guidance: fallback ?? undefined }));

  const result = await Promise.race([request, timeout]);
  const guidance = result.guidance?.trim();
  return guidance || fallback;
}
